import type { ClarificationCardPayload } from '@diagon-alley/common';
import type { SubmitToolResponseArgs } from '@hooks/useReportingThread';
import {
    Button,
    Card,
    Checkbox,
    Group,
    Radio,
    Stack,
    Text,
} from '@mantine/core';
import { Question } from '@phosphor-icons/react';
import { useState } from 'react';

import styles from './ReportsContainer.module.css';

interface ClarificationCardProps {
    payload: ClarificationCardPayload;
    toolCallId: string;
    onSubmit?: (args: SubmitToolResponseArgs) => Promise<void>;
}

/**
 * Interactive clarification card. The user picks one (or many) options
 * and clicks Confirm; the structured selection is round-tripped to the
 * backend via POST /tool-response, which resumes the agent loop with
 * the selection in scope.
 */
export default function ClarificationCard({
    payload,
    toolCallId,
    onSubmit,
}: ClarificationCardProps) {
    const initialSelection = payload.options
        .filter((o) => o.default)
        .map((o) => o.id);
    const [selected, setSelected] = useState<string[]>(initialSelection);
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false);

    const toggleSingle = (id: string) => setSelected([id]);
    const toggleMulti = (id: string) =>
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        );

    const onConfirm = async () => {
        if (selected.length === 0 || submitted) return;
        setSubmitted(true);
        if (!onSubmit) return; // display-only fallback (no streaming round-trip)

        setBusy(true);
        try {
            const value = payload.multiSelect ? selected : selected[0];
            const labels = labelsFor(payload, selected);
            await onSubmit({
                toolCallId,
                response: { fieldKey: payload.fieldKey, value },
                displayLabel: `${payload.fieldKey}: ${labels}`,
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card withBorder radius="md" padding="sm" className={styles.clarificationCard}>
            <Stack gap="xs">
                <Group gap="xs" align="center">
                    <Question size={16} weight="duotone" />
                    <Text size="sm" fw={600}>
                        {payload.title}
                    </Text>
                </Group>
                {payload.helpText && (
                    <Text size="xs" c="dimmed">
                        {payload.helpText}
                    </Text>
                )}

                <Stack gap={6}>
                    {payload.multiSelect
                        ? payload.options.map((opt) => (
                              <Checkbox
                                  key={opt.id}
                                  label={opt.label}
                                  checked={selected.includes(opt.id)}
                                  onChange={() => toggleMulti(opt.id)}
                                  disabled={submitted}
                                  size="sm"
                              />
                          ))
                        : payload.options.map((opt) => (
                              <Radio
                                  key={opt.id}
                                  label={opt.label}
                                  checked={selected[0] === opt.id}
                                  onChange={() => toggleSingle(opt.id)}
                                  disabled={submitted}
                                  size="sm"
                              />
                          ))}
                </Stack>

                <Group justify="flex-end">
                    {submitted ? (
                        <Text size="xs" c="dimmed">
                            Selected: {labelsFor(payload, selected)}.
                        </Text>
                    ) : (
                        <Button
                            size="xs"
                            onClick={onConfirm}
                            disabled={selected.length === 0}
                            loading={busy}
                        >
                            Confirm
                        </Button>
                    )}
                </Group>
            </Stack>
        </Card>
    );
}

function labelsFor(
    payload: ClarificationCardPayload,
    ids: string[],
): string {
    const labels = ids
        .map((id) => payload.options.find((o) => o.id === id)?.label ?? id)
        .filter(Boolean);
    return labels.join(', ') || '(none)';
}
